import { useState, useEffect } from 'react';
import { Network, Copy, Check, ShieldAlert } from 'lucide-react';
import Modal from '../common/Modal';
import connectorService from '../../services/connector.service';
import type { AvailableEdge } from '../../services/connector.service';
import { EDGE_API_URL } from '../../utils/edgeApiUrl';

export interface ConnectorFormData {
  id?: string;
  connectorName: string;
  tenantId: string;
  description: string;
  deploymentRegion: string;
  wanIp: string;
  hubEdgeId: string;
  hubType?: 'dedicated' | 'mtge';
  autoApprove: boolean;
}

export interface ConnectorCreationResult {
  id: string;
  serialNumber: string;
  connectorName: string;
  status: string;
  publicKey: string;
  deploymentPassword?: string;
  provisionToken?: string;
  warning?: string;
}

interface ConnectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ConnectorFormData) => Promise<ConnectorCreationResult | void>;
  connector?: ConnectorFormData;
  tenantId: string;
  mode: 'add' | 'edit';
}

const defaultFormData: ConnectorFormData = {
  connectorName: '',
  tenantId: '',
  description: '',
  deploymentRegion: '',
  wanIp: '',
  hubEdgeId: '',
  autoApprove: false,
};

const CopyableField = ({ label, value, mono, highlight }: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <button
          onClick={handleCopy}
          className="inline-flex items-center space-x-1 text-xs text-gray-500 hover:text-primary-600 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <p className={`text-sm ${highlight ? 'text-green-800 font-semibold' : 'text-gray-900'} ${mono ? 'font-mono' : ''} break-all`}>
        {value}
      </p>
    </div>
  );
};

const ConnectorModal = ({ isOpen, onClose, onSave, connector, tenantId, mode }: ConnectorModalProps) => {
  const [formData, setFormData] = useState<ConnectorFormData>(connector || { ...defaultFormData, tenantId });
  const [saving, setSaving] = useState(false);
  const [creationResult, setCreationResult] = useState<ConnectorCreationResult | null>(null);
  const [availableEdges, setAvailableEdges] = useState<AvailableEdge[]>([]);
  const [loadingEdges, setLoadingEdges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(connector || { ...defaultFormData, tenantId });
      setCreationResult(null);
    }
  }, [isOpen, connector, tenantId]);

  useEffect(() => {
    if (isOpen && tenantId) {
      setLoadingEdges(true);
      connectorService.getAvailableEdges(tenantId, mode === 'edit' ? connector?.id : undefined)
        .then(setAvailableEdges)
        .catch(() => setAvailableEdges([]))
        .finally(() => setLoadingEdges(false));
    }
  }, [isOpen, tenantId]);

  const handleChange = (field: keyof ConnectorFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const result = await onSave({ ...formData, tenantId });
      if (mode === 'add' && result && 'serialNumber' in result) {
        setCreationResult(result);
      } else {
        onClose();
      }
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  // Credentials view after successful creation
  if (creationResult) {
    const credentialsFooter = (
      <div className="flex items-center justify-end">
        <button onClick={onClose} className="btn-primary">Done</button>
      </div>
    );

    const token = creationResult.provisionToken || creationResult.deploymentPassword || '';
    const installerCmd = token
      ? `sudo securelink-installer install \\\n  --token ${token} \\\n  --url ${EDGE_API_URL}`
      : '';

    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Connector Created" size="lg" footer={credentialsFooter}>
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Save these credentials now</p>
                <p className="text-sm text-amber-700 mt-1">
                  The deployment token will only be shown once and cannot be retrieved later.
                </p>
              </div>
            </div>
          </div>

          <CopyableField label="Connector Name" value={creationResult.connectorName} />
          <CopyableField label="Serial Number" value={creationResult.serialNumber} mono />
          {creationResult.provisionToken && (
            <CopyableField label="Deployment Token" value={creationResult.provisionToken} mono highlight />
          )}

          {installerCmd && (
            <div className="border-t pt-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Installer Command</p>
              <CopyableField label="" value={installerCmd} mono />
            </div>
          )}
        </div>
      </Modal>
    );
  }

  const isValid = formData.connectorName.trim() !== '';

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">Cancel</button>
      <button
        onClick={handleSubmit}
        disabled={saving || !isValid}
        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
      >
        <Network className="w-4 h-4" />
        <span>{saving ? 'Saving...' : mode === 'add' ? 'Create Connector' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Connector' : `Edit: ${connector?.connectorName}`}
      size="lg"
      footer={footer}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Connector Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.connectorName}
              onChange={(e) => handleChange('connectorName', e.target.value)}
              placeholder="e.g., AWS-SYD-VPC-01"
              className="input-base w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hub Edge</label>
            <select
              value={formData.hubEdgeId}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedEdge = availableEdges.find(edge => edge.id === selectedId);
                setFormData(prev => ({
                  ...prev,
                  hubEdgeId: selectedId,
                  hubType: selectedEdge?.type || 'dedicated',
                }));
              }}
              className="input-base w-full"
              disabled={loadingEdges}
            >
              <option value="">{loadingEdges ? 'Loading edges...' : 'No hub edge (standalone)'}</option>
              {availableEdges.map((edge) => (
                <option key={edge.id} value={edge.id}>
                  {edge.name || 'Unnamed'} ({edge.type === 'mtge' ? 'MTGE' : 'Edge'})
                </option>
              ))}
            </select>
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 space-y-1">
              <div>
                Hubs include dedicated <strong>Edges</strong> with the IoT Gateway Server (wg0)
                enabled, and <strong>MTGEs</strong> with an active tenant assignment.
                Enable the IoT Gateway Server via the edge's Configuration page → Gateways.
              </div>
              <div>
                Connectors use <strong>kernel WireGuard</strong> for their hub link. For
                site-to-site IPSec between two locations, deploy a dedicated Edge VM and
                use E2E Peering instead.
              </div>
            </div>
            {!loadingEdges && availableEdges.length === 0 && (
              <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                No eligible hubs found for this tenant. Enable the IoT Gateway Server on an
                Edge, or assign this tenant to an MTGE via <strong>/mtges</strong>.
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WAN IP</label>
            <input
              type="text"
              value={formData.wanIp}
              onChange={(e) => handleChange('wanIp', e.target.value)}
              placeholder="e.g., 203.0.113.50"
              className="input-base w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Deployment Region</label>
            <input
              type="text"
              value={formData.deploymentRegion}
              onChange={(e) => handleChange('deploymentRegion', e.target.value)}
              placeholder="e.g., aws-ap-southeast-2"
              className="input-base w-full"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description..."
              className="input-base w-full"
              rows={2}
            />
          </div>

          {mode === 'add' && (
            <div className="md:col-span-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.autoApprove}
                  onChange={(e) => handleChange('autoApprove', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Auto-Approve</span>
                  <p className="text-xs text-gray-500">Skip manual approval — generates deployment password immediately</p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConnectorModal;
